import { Component, OnInit, effect, inject } from '@angular/core';
import { RecentClaimsService } from '../../services/recent-claims/recent-claims.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { ClaimLabelComponent } from '../core/labels/claim-label.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { EmptyStateComponent } from '../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../core/panels/loading-panel.component';
import { ColumnComponent } from '../core/table/column.component';
import { TableComponent } from '../core/table/table.component';
import { HomeCardComponent } from './home-card.component';

@Component({
  selector: 'app-history-card',
  imports: [
    HomeCardComponent,
    TableComponent,
    ColumnComponent,
    ClaimLabelComponent,
    ActionRowComponent,
    ButtonComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-home-card
      type="Large"
      theme="Light"
      [padding]="false"
      [minHeight]="'450px'"
      classes="md:tw-h-[470px]"
    >
      <div class="tw-pb-6">
        <h4>History</h4>
      </div>

      <app-loading-panel
        [state]="state"
        loadingStyle="Skeleton"
        [loadingHeight]="240"
        [error]="error"
      >
        <ng-template #empty>
          <app-empty-state
            icon="fa-light fa-history"
            label="Empty history"
            height="300px"
          >
            <span
              >Previously viewed claims will appear here. Try
              <app-link-label (click)="searchClick()">searching</app-link-label>
              for a claim.</span
            >
          </app-empty-state>
        </ng-template>
        <ng-template #loaded>
          <div class="tw-min-h-[280px] md:tw-h-[300px]">
            <app-table
              [dataSource]="claimsData"
              [pageSize]="5"
              [scrollable]="true"
              scrollHeight="278px"
              [tableStyle]="{ width: '100%', 'table-layout': 'fixed' }"
              [showFilter]="false"
              [showSort]="false"
              [showRecordLabel]="false"
              [showPaginator]="false"
              [loadingHeight]="240"
              theme="Light"
              [ariaTableLabel]="'History'"
              mobileLayout="list"
            >
              <app-column name="fileNumber" label="Claim" width="40%">
                <ng-template let-data>
                  <app-claim-label [claim]="data" />
                </ng-template>
              </app-column>
              <app-column
                name="claimantLastName"
                label="Name"
                width="30%"
                classes="history-name-column"
                [wrap]="true"
              >
                <ng-template let-data>
                  <span
                    class="tw-block tw-min-w-0 tw-break-words tw-whitespace-normal"
                  >
                    {{
                      (data.claimantFirstName || '') +
                        (data.claimantFirstName && data.claimantLastName
                          ? ' '
                          : '') +
                        (data.claimantLastName || '')
                    }}
                  </span>
                </ng-template>
              </app-column>
              <app-column
                name="dateAccessed"
                label="Date accessed"
                format="d"
                width="30%"
              />
            </app-table>
          </div>
        </ng-template>
      </app-loading-panel>

      @if (claimsData.length > 0) {
        <app-action-row class="tw-pt-4 tw-px-4">
          <app-button
            left1
            label="More"
            aria-label="recently viewed more button"
            (click)="navigateToView()"
          />
        </app-action-row>
      }
    </app-home-card>
  `,
  styles: `
    :host ::ng-deep td.history-name-column {
      vertical-align: top;
    }

    :host ::ng-deep app-table tr {
      height: auto;
      min-height: 40px;
    }
  `,
})
export class HistoryCardComponent extends BaseComponent implements OnInit {
  recentClaims = inject(RecentClaimsService);

  protected state: LoadingState = 'Loading';

  claimsData: any[] = [];

  error: any = null;

  constructor() {
    super();
    effect(() => {
      const data = this.recentClaims.claims();
      if (data) {
        this.claimsData = (data.rows || []).slice(0, 5);
        this.state = this.claimsData.length > 0 ? 'Loaded' : 'Empty';
      }
    });
  }

  async ngOnInit() {
    await this.loadClaims();
    this.view.hideLoading();
  }

  async loadClaims() {
    this.error = null;
    this.state = 'Loading';
    try {
      await this.recentClaims.load();
    } catch (error) {
      this.error = error;
      this.state = 'Empty';
    }
  }

  navigateToView() {
    this.router.navigate(['/view'], { queryParams: { tab: 'history' } });
  }

  async searchClick(): Promise<void> {
    await this.router.navigate(['/view'], { queryParams: { tab: 'search' } });
    document.body.scrollTo({ top: 0, behavior: 'instant' });
  }
}
